// apps/client/src/utils/periodUtils.ts
// Utility functions for relative time period calculations
// Used by DashboardPage.tsx for the Yahoo Finance-style period selector

import type { RelativePeriod, MonthRange } from '../types';

/**
 * Format a Date as YYYY-MM string
 */
function formatYearMonth(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

/**
 * Subtract months from a date and return YYYY-MM format
 */
function subtractMonths(date: Date, months: number): string {
  const result = new Date(date);
  result.setMonth(result.getMonth() - months);
  return formatYearMonth(result);
}

/**
 * Calculate the number of months between two YYYY-MM strings (inclusive)
 */
function getMonthDiff(start: string, end: string): number {
  const [sY, sM] = start.split('-').map(Number);
  const [eY, eM] = end.split('-').map(Number);
  return (eY - sY) * 12 + (eM - sM);
}

/**
 * Calculate month range from relative period
 *
 * @param period - Relative period (3M, 6M, YTD, 1Y, 2Y, 5Y, ALL)
 * @param referenceDate - Date to calculate from (defaults to today)
 * @param earliestMonth - Earliest month with data (for ALL period)
 * @returns MonthRange with startMonth and endMonth in YYYY-MM format
 */
export function getMonthRangeFromPeriod(
  period: RelativePeriod,
  referenceDate: Date = new Date(),
  earliestMonth?: string
): MonthRange {
  const endMonth = formatYearMonth(referenceDate);

  switch (period) {
    case '3M':
      // Current month + 2 prior = 3 months total
      return { startMonth: subtractMonths(referenceDate, 2), endMonth };

    case '6M':
      // Current month + 5 prior = 6 months total
      return { startMonth: subtractMonths(referenceDate, 5), endMonth };

    case 'YTD':
      // Calendar year-to-date: Jan 1 to current month
      return { startMonth: `${referenceDate.getFullYear()}-01`, endMonth };

    case '1Y':
      // Current month + 11 prior = 12 months total
      return { startMonth: subtractMonths(referenceDate, 11), endMonth };

    case '2Y':
      // Current month + 23 prior = 24 months total
      return { startMonth: subtractMonths(referenceDate, 23), endMonth };

    case '5Y':
      // Current month + 59 prior = 60 months total
      return { startMonth: subtractMonths(referenceDate, 59), endMonth };

    case 'ALL':
      // Use earliest data month if provided, otherwise fallback to 5Y
      return { startMonth: earliestMonth || subtractMonths(referenceDate, 59), endMonth };

    default:
      // Default to 1Y
      return { startMonth: subtractMonths(referenceDate, 11), endMonth };
  }
}

/**
 * Calculate previous period for comparison
 * For relative periods, the "previous" period is the same duration immediately before
 *
 * Example: 1Y (Feb 2025 - Jan 2026) -> Previous: Feb 2024 - Jan 2025
 */
export function getPreviousPeriodRange(period: RelativePeriod, current: MonthRange): MonthRange {
  const monthCount = getMonthDiff(current.startMonth, current.endMonth) + 1;

  // Calculate previous period by shifting back by the period duration
  const [startYear, startMonthNum] = current.startMonth.split('-').map(Number);

  // End of previous period is the month before current start
  const prevEndDate = new Date(startYear, startMonthNum - 2, 1); // month is 0-indexed, so -2 gives us month before

  // Start of previous period
  const prevStartDate = new Date(prevEndDate);
  prevStartDate.setMonth(prevStartDate.getMonth() - (monthCount - 1));

  return {
    startMonth: formatYearMonth(prevStartDate),
    endMonth: formatYearMonth(prevEndDate),
  };
}

/**
 * Get human-readable label for the period range
 * Example: "Feb 2025 - Jan 2026"
 */
export function getPeriodLabel(period: RelativePeriod, range: MonthRange): string {
  const formatMonth = (ym: string) => {
    const [year, month] = ym.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    return date.toLocaleString('default', { month: 'short', year: 'numeric' });
  };

  return `${formatMonth(range.startMonth)} - ${formatMonth(range.endMonth)}`;
}

/**
 * Get comparison label for the period
 * Example: "vs prior 1Y"
 */
export function getPeriodComparisonLabel(period: RelativePeriod): string {
  const labels: Record<RelativePeriod, string> = {
    '3M': 'vs prior 3M',
    '6M': 'vs prior 6M',
    'YTD': 'vs prior YTD',
    '1Y': 'vs prior year',
    '2Y': 'vs prior 2Y',
    '5Y': 'vs prior 5Y',
    'ALL': 'vs prior period',
  };
  return labels[period];
}

/**
 * Generate array of months in a range for chart data
 * Returns array of { yearMonth: 'YYYY-MM', label: 'Jan' }
 */
export function generateMonthsInRange(
  startMonth: string,
  endMonth: string
): Array<{ yearMonth: string; label: string }> {
  const months: Array<{ yearMonth: string; label: string }> = [];
  const [sY, sM] = startMonth.split('-').map(Number);
  const [eY, eM] = endMonth.split('-').map(Number);

  let year = sY;
  let month = sM;

  while (year < eY || (year === eY && month <= eM)) {
    const yearMonth = `${year}-${String(month).padStart(2, '0')}`;
    const date = new Date(year, month - 1, 1);
    const label = date.toLocaleString('default', { month: 'short' });
    months.push({ yearMonth, label });

    month++;
    if (month > 12) {
      month = 1;
      year++;
    }
  }

  return months;
}
